import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight, Search } from 'lucide-react';
import { useAdminAuth } from '../lib/auth-context';
import { NAV_GROUPS, visibleGroups, groupKeyForPath } from './nav-model';
import { useNavGroupState } from './nav-group-state';
import { SidebarFooter } from './sidebar-footer';
import { useCommandPaletteStore } from '../stores/command-palette-store';

export function Sidebar() {
  const { hasPerm } = useAdminAuth();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const groups = visibleGroups(NAV_GROUPS, hasPerm);
  const activeKey = groupKeyForPath(groups, currentPath);
  const { isExpanded, toggle } = useNavGroupState(activeKey);

  return (
    <nav
      aria-label="Main navigation"
      className="flex w-52 shrink-0 flex-col h-full border-r border-border bg-surface py-3 px-2"
    >
      <div className="px-2 pb-4 mb-2 border-b border-border">
        <span className="text-eyebrow uppercase text-muted">HeyRoot Admin</span>
      </div>

      {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- design-exception: palette trigger showing its own shortcut hint */}
      <button
        type="button"
        onClick={() => useCommandPaletteStore.getState().openPalette()}
        className="mb-2 flex items-center gap-2 rounded-control border border-border px-2.5 py-1.5 text-body-sm text-muted hover:bg-surface-sunken hover:text-primary transition-colors duration-[var(--duration-fast)]"
      >
        <Search aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">Search…</span>
        <kbd className="text-caption">Ctrl+K</kbd>
      </button>

      <ul role="list" className="flex-1 space-y-0.5 list-none p-0 m-0 overflow-y-auto">
        {groups.map((group) => {
          const open = isExpanded(group.key);
          return (
            <li key={group.key}>
              {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- design-exception: nav disclosure row; Button primitive carries CTA weight unsuitable for shell chrome */}
              <button
                type="button"
                onClick={() => toggle(group.key)}
                aria-expanded={open}
                className="flex w-full items-center gap-2 rounded-control px-2.5 py-1.5 text-body-sm-emphasis text-secondary hover:bg-surface-sunken hover:text-primary transition-colors duration-[var(--duration-fast)]"
              >
                <group.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="flex-1 text-left">{group.label}</span>
                <ChevronRight
                  aria-hidden="true"
                  className={
                    open
                      ? 'h-3.5 w-3.5 shrink-0 rotate-90 transition-transform duration-[var(--duration-fast)]'
                      : 'h-3.5 w-3.5 shrink-0 transition-transform duration-[var(--duration-fast)]'
                  }
                />
              </button>

              {open && (
                <ul role="list" className="animate-nav-submenu-in list-none p-0 m-0 pl-4">
                  {group.items.map((item) => {
                    const isActive =
                      currentPath === item.to || currentPath.startsWith(`${item.to}/`);
                    return (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          aria-current={isActive ? 'page' : undefined}
                          className={
                            isActive
                              ? 'flex items-center rounded-control px-2.5 py-1.5 text-body-sm-emphasis text-primary bg-surface-sunken transition-colors duration-[var(--duration-fast)]'
                              : 'flex items-center rounded-control px-2.5 py-1.5 text-body-sm text-secondary hover:bg-surface-sunken hover:text-primary transition-colors duration-[var(--duration-fast)]'
                          }
                        >
                          {item.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      <SidebarFooter />
    </nav>
  );
}
